import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, RefreshCw, Crown, Megaphone, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

export const PlanModal = ({ isOpen, onClose, plan, suggestedData, onSave }) => {
  const [form, setForm] = useState({
    name: '', description: '', monthly_price: 0, annual_price: 0, one_time_price: 0,
    billing_type: 'recurring',
    account_limit: 1, chatbot_limit: 1, message_limit: 100, document_limit: 10,
    document_size_mb: 10, duration_days: 30, features: '', is_popular: false, is_featured: false,
    display_order: 0, is_active: true, show_advertisement: false,
    advertisement_config: { headline: '', description: '', cta_text: 'Get Started', cta_url: '/pricing', image_url: '', end_date: '' },
    feature_access: {
      leads_crm: true,
      analytics: true,
      automations: true,
      email_channel: true,
      webhooks: true,
      appointments: true,
      social_channels: true,
      whatsapp_channel: true,
      facebook_channel: true,
      instagram_channel: true,
      telegram_channel: true,
      api_sources: true
    }
  });

  const [targetMargin, setTargetMargin] = useState(30);

  // Cost estimation constants
  const COST_PER_1000_MESSAGES = 0.15;
  const COST_PER_GB_STORAGE = 0.10;
  const COST_PER_CHATBOT = 0.50;
  const PLATFORM_OVERHEAD = 2.00;

  const estimatedCosts = {
    aiCost: (form.message_limit / 1000) * COST_PER_1000_MESSAGES,
    storageCost: (form.document_limit * form.document_size_mb / 1024) * COST_PER_GB_STORAGE,
    infraCost: form.chatbot_limit * COST_PER_CHATBOT,
    overhead: PLATFORM_OVERHEAD,
  };
  estimatedCosts.total = estimatedCosts.aiCost + estimatedCosts.storageCost + estimatedCosts.infraCost + estimatedCosts.overhead;

  const profitMargin = form.monthly_price > 0
    ? ((form.monthly_price - estimatedCosts.total) / form.monthly_price * 100)
    : 0;

  const getProfitStatus = () => {
    if (form.monthly_price === 0) return { color: 'slate', label: 'Set a price', icon: '$' };
    if (form.monthly_price < estimatedCosts.total) return { color: 'red', label: 'Loss', icon: '!' };
    if (profitMargin < 20) return { color: 'amber', label: 'Low margin', icon: '~' };
    if (profitMargin < 50) return { color: 'emerald', label: 'Good', icon: '+' };
    return { color: 'blue', label: 'Excellent', icon: '★' };
  };

  const status = getProfitStatus();
  const suggestedMinPrice = Math.ceil(estimatedCosts.total * (1 + targetMargin / 100) * 100) / 100;

  useEffect(() => {
    if (plan) {
      const features = Array.isArray(plan.features) ? plan.features.join('\n') : (plan.features || '');
      requestAnimationFrame(() => setForm({
        ...plan,
        features,
        billing_type: plan.billing_type || 'recurring',
        one_time_price: plan.one_time_price || 0,
        is_featured: plan.is_featured || false,
        show_advertisement: plan.show_advertisement || false,
        advertisement_config: plan.advertisement_config || { headline: '', description: '', cta_text: 'Get Started', cta_url: '/pricing', image_url: '', end_date: '' }
      }));
    } else if (suggestedData) {
      const defaultFeatures = [
        `${suggestedData.chatbot_limit} AI Employee${suggestedData.chatbot_limit > 1 ? 's' : ''}`,
        `${suggestedData.message_limit.toLocaleString()} Messages/month`,
        `${suggestedData.document_limit} Documents`,
        `${suggestedData.document_size_mb}MB Storage`
      ].join('\n');
      requestAnimationFrame(() => setForm({
        ...suggestedData,
        features: defaultFeatures,
        billing_type: 'recurring',
        one_time_price: 0,
        is_featured: false,
        show_advertisement: false,
        advertisement_config: { headline: '', description: '', cta_text: 'Get Started', cta_url: '/pricing', end_date: '' }
      }));
    } else {
      requestAnimationFrame(() => setForm({
        name: '', description: '', monthly_price: 0, annual_price: 0, one_time_price: 0,
        billing_type: 'recurring',
        account_limit: 1, chatbot_limit: 1, message_limit: 100, document_limit: 10,
        document_size_mb: 10, duration_days: 30, features: '', is_popular: false, is_featured: false,
        display_order: 0, is_active: true, show_advertisement: false,
        advertisement_config: { headline: '', description: '', cta_text: 'Get Started', cta_url: '/pricing', image_url: '', end_date: '' },
        feature_access: {
          leads_crm: true, analytics: true, automations: true, email_channel: true,
          webhooks: true, appointments: true, social_channels: true,
          whatsapp_channel: true, facebook_channel: true, instagram_channel: true, telegram_channel: true,
          api_sources: true
        }
      }));
    }
  }, [plan, suggestedData, isOpen]);

  const handleSave = () => {
    const featuresArray = form.features.split('\n').map(f => f.trim()).filter(f => f);
    const saveData = {
      ...form,
      features: featuresArray,
      feature_access: form.feature_access || {},
      billing_type: form.billing_type,
      one_time_price: form.billing_type === 'one_time' ? form.one_time_price : null,
      is_featured: form.is_featured,
      show_advertisement: form.show_advertisement,
      advertisement_config: form.show_advertisement ? form.advertisement_config : null
    };
    onSave(saveData);
  };

  const featureToggles = [
    { key: 'leads_crm', label: 'Leads CRM' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'automations', label: 'Automations' },
    { key: 'email_channel', label: 'Email Channel' },
    { key: 'webhooks', label: 'Webhooks' },
    { key: 'appointments', label: 'Appointments' },
    { key: 'social_channels', label: 'Social Channels (master)' },
    // iter128.1 — per-platform gates so admins can flip each social channel
    // independently while the platform is being rolled out.
    { key: 'whatsapp_channel', label: 'WhatsApp Channel' },
    { key: 'facebook_channel', label: 'Facebook Channel' },
    { key: 'instagram_channel', label: 'Instagram Channel' },
    { key: 'telegram_channel', label: 'Telegram Channel' },
    { key: 'api_sources', label: 'API Sources' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900">
            {plan ? 'Edit Plan' : suggestedData ? `Create ${suggestedData.name} Plan` : 'Create New Plan'}
          </DialogTitle>
          {suggestedData && !plan && (
            <p className="text-sm text-emerald-600 mt-1">Pre-filled from Smart Pricing Calculator</p>
          )}
        </DialogHeader>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form Fields - 2 columns */}
          <div className="lg:col-span-2 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-slate-700">Plan Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="Professional" /></div>
              <div className="space-y-2"><Label className="text-slate-700">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="For growing teams" /></div>

              {/* Billing Type Selector */}
              <div className="space-y-2 md:col-span-2">
                <Label className="text-slate-700">Billing Type</Label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${form.billing_type === 'recurring' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                    <input type="radio" name="billing_type" value="recurring" checked={form.billing_type === 'recurring'} onChange={() => setForm({ ...form, billing_type: 'recurring' })} className="sr-only" />
                    <RefreshCw className={`w-5 h-5 ${form.billing_type === 'recurring' ? 'text-blue-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${form.billing_type === 'recurring' ? 'text-blue-700' : 'text-slate-700'}`}>Recurring</p>
                      <p className="text-xs text-slate-500">Monthly/Annual subscription</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${form.billing_type === 'one_time' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                    <input type="radio" name="billing_type" value="one_time" checked={form.billing_type === 'one_time'} onChange={() => setForm({ ...form, billing_type: 'one_time' })} className="sr-only" />
                    <Crown className={`w-5 h-5 ${form.billing_type === 'one_time' ? 'text-amber-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${form.billing_type === 'one_time' ? 'text-amber-700' : 'text-slate-700'}`}>Lifetime</p>
                      <p className="text-xs text-slate-500">One-time payment, forever access</p>
                    </div>
                  </label>
                </div>
              </div>

              {form.billing_type === 'recurring' ? (
                <>
                  <div className="space-y-2"><Label className="text-slate-700">Monthly Price ($)</Label><Input type="number" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
                  <div className="space-y-2"><Label className="text-slate-700">Annual Price/Month ($)</Label><Input type="number" value={form.annual_price} onChange={(e) => setForm({ ...form, annual_price: parseFloat(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
                </>
              ) : (
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-slate-700">One-Time Price ($)</Label>
                  <Input type="number" value={form.one_time_price} onChange={(e) => setForm({ ...form, one_time_price: parseFloat(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" placeholder="299" />
                  <p className="text-xs text-amber-600 mt-1">Users pay once and get lifetime access with same limits as recurring plan.</p>
                </div>
              )}

              <div className="space-y-2"><Label className="text-slate-700">Account Limit</Label><Input type="number" value={form.account_limit} onChange={(e) => setForm({ ...form, account_limit: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
              <div className="space-y-2"><Label className="text-slate-700">AI Employee Limit</Label><Input type="number" value={form.chatbot_limit} onChange={(e) => setForm({ ...form, chatbot_limit: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
              <div className="space-y-2"><Label className="text-slate-700">Message Limit/Month</Label><Input type="number" value={form.message_limit} onChange={(e) => setForm({ ...form, message_limit: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
              <div className="space-y-2"><Label className="text-slate-700">Document Limit</Label><Input type="number" value={form.document_limit} onChange={(e) => setForm({ ...form, document_limit: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
              <div className="space-y-2"><Label className="text-slate-700">Document Size Limit (MB)</Label><Input type="number" value={form.document_size_mb} onChange={(e) => setForm({ ...form, document_size_mb: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
              <div className="space-y-2"><Label className="text-slate-700">Display Order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 text-slate-900" /></div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Features (one per line)</Label>
              <textarea
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                className="w-full h-20 px-3 py-2 bg-white border border-slate-300 rounded-md text-slate-900 placeholder-slate-400 resize-none"
                placeholder="5 AI Employees&#10;10,000 Messages/month&#10;Priority Support"
              />
            </div>

            {/* Feature Access Controls */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Label className="text-slate-700 font-semibold">Feature Access</Label>
              <p className="text-xs text-slate-500">Select which platform features are available for this plan</p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {featureToggles.map(({ key, label }) => (
                  <div key={`feature-toggle-${key}`} className="flex items-center gap-2">
                    <Switch
                      checked={form.feature_access?.[key] !== false}
                      onCheckedChange={(c) => setForm({ ...form, feature_access: { ...form.feature_access, [key]: c } })}
                    />
                    <Label className="text-slate-600 text-sm">{label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Promotional Advertisement Settings */}
            <div className="space-y-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-amber-600" />
                  <Label className="text-slate-700 font-semibold">Promotional Advertisement</Label>
                </div>
                <Switch
                  checked={form.show_advertisement}
                  onCheckedChange={(c) => setForm({ ...form, show_advertisement: c })}
                />
              </div>
              <p className="text-xs text-slate-500">Show this plan in homepage promotional popup</p>

              {form.show_advertisement && (
                <div className="space-y-3 mt-3 pt-3 border-t border-amber-200">
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Background Image (optional)</Label>
                    <p className="text-xs text-slate-500">When set, the image becomes the popup background. Headline, description and CTA appear over it.</p>
                    {form.advertisement_config?.image_url ? (
                      <div className="relative rounded-lg overflow-hidden border border-amber-300 bg-slate-50">
                        <img
                          src={form.advertisement_config.image_url.startsWith('http') ? form.advertisement_config.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.advertisement_config.image_url}`}
                          alt="Ad background preview"
                          className="w-full h-40 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, advertisement_config: { ...form.advertisement_config, image_url: '' } })}
                          className="absolute top-2 right-2 bg-white/90 hover:bg-white text-rose-600 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm border border-rose-200"
                          data-testid="ad-image-remove"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 rounded-lg border-2 border-dashed border-amber-300 bg-white/60 hover:bg-white cursor-pointer transition-colors">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          data-testid="ad-image-upload"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('Image too large (max 5MB)');
                              return;
                            }
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const res = await api.post('/admin/advertisements/upload-image', fd, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setForm({ ...form, advertisement_config: { ...form.advertisement_config, image_url: res.data.image_url } });
                              toast.success('Image uploaded');
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Upload failed');
                            }
                            e.target.value = '';
                          }}
                        />
                        <Megaphone className="w-7 h-7 text-amber-500 mb-1.5" />
                        <span className="text-sm font-semibold text-slate-700">Click to upload background image</span>
                        <span className="text-xs text-slate-500 mt-0.5">PNG, JPG, WEBP up to 5MB · 4:3 or 16:9 looks best</span>
                      </label>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Headline</Label>
                    <Input
                      value={form.advertisement_config?.headline || ''}
                      onChange={(e) => setForm({ ...form, advertisement_config: { ...form.advertisement_config, headline: e.target.value } })}
                      className="bg-white border-amber-300 text-slate-900"
                      placeholder="Early Bird Special - 50% Off!"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Description</Label>
                    <Input
                      value={form.advertisement_config?.description || ''}
                      onChange={(e) => setForm({ ...form, advertisement_config: { ...form.advertisement_config, description: e.target.value } })}
                      className="bg-white border-amber-300 text-slate-900"
                      placeholder="Get lifetime access at founding member pricing"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-slate-600 text-sm">CTA Button Text</Label>
                      <Input
                        value={form.advertisement_config?.cta_text || 'Get Started'}
                        onChange={(e) => setForm({ ...form, advertisement_config: { ...form.advertisement_config, cta_text: e.target.value } })}
                        className="bg-white border-amber-300 text-slate-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600 text-sm">CTA URL</Label>
                      <Input
                        value={form.advertisement_config?.cta_url || '/pricing'}
                        onChange={(e) => setForm({ ...form, advertisement_config: { ...form.advertisement_config, cta_url: e.target.value } })}
                        className="bg-white border-amber-300 text-slate-900"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Offer End Date</Label>
                    <Input
                      type="datetime-local"
                      value={form.advertisement_config?.end_date || ''}
                      onChange={(e) => setForm({ ...form, advertisement_config: { ...form.advertisement_config, end_date: e.target.value } })}
                      className="bg-white border-amber-300 text-slate-900"
                    />
                    <p className="text-xs text-amber-600">Countdown timer will display on homepage popup</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} /><Label className="text-slate-700">Active</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_popular} onCheckedChange={(c) => setForm({ ...form, is_popular: c })} /><Label className="text-slate-700">Most Popular</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={(c) => setForm({ ...form, is_featured: c })} /><Label className="text-slate-700">Featured</Label></div>
            </div>
          </div>

          {/* Cost Calculator Sidebar */}
          <div className="lg:col-span-1">
            <div className={`p-4 rounded-xl border-2 ${
              status.color === 'red' ? 'bg-red-50 border-red-300' :
              status.color === 'amber' ? 'bg-amber-50 border-amber-300' :
              status.color === 'emerald' ? 'bg-emerald-50 border-emerald-300' :
              status.color === 'blue' ? 'bg-blue-50 border-blue-300' :
              'bg-slate-50 border-slate-300'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className={`w-5 h-5 ${
                  status.color === 'red' ? 'text-red-600' :
                  status.color === 'amber' ? 'text-amber-600' :
                  status.color === 'emerald' ? 'text-emerald-600' :
                  status.color === 'blue' ? 'text-blue-600' :
                  'text-slate-600'
                }`} />
                <h4 className="font-semibold text-slate-900">Cost Calculator</h4>
              </div>

              {/* Cost Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">AI API ({form.message_limit.toLocaleString()} msgs)</span>
                  <span className="text-slate-900">${estimatedCosts.aiCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Storage ({form.document_limit} docs)</span>
                  <span className="text-slate-900">${estimatedCosts.storageCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Infra ({form.chatbot_limit} agents)</span>
                  <span className="text-slate-900">${estimatedCosts.infraCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Platform overhead</span>
                  <span className="text-slate-900">${estimatedCosts.overhead.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-300 pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span className="text-slate-700">Est. Cost/User</span>
                    <span className="text-slate-900">${estimatedCosts.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Profit Analysis */}
              <div className={`mt-4 p-3 rounded-lg ${
                status.color === 'red' ? 'bg-red-100' :
                status.color === 'amber' ? 'bg-amber-100' :
                status.color === 'emerald' ? 'bg-emerald-100' :
                status.color === 'blue' ? 'bg-blue-100' :
                'bg-slate-100'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{status.icon}</span>
                  <span className={`font-semibold ${
                    status.color === 'red' ? 'text-red-700' :
                    status.color === 'amber' ? 'text-amber-700' :
                    status.color === 'emerald' ? 'text-emerald-700' :
                    status.color === 'blue' ? 'text-blue-700' :
                    'text-slate-700'
                  }`}>{status.label}</span>
                </div>
                <div className="text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Your Price:</span>
                    <span className="font-semibold text-slate-900">${form.monthly_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Profit/User:</span>
                    <span className={`font-semibold ${form.monthly_price >= estimatedCosts.total ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${(form.monthly_price - estimatedCosts.total).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Margin:</span>
                    <span className={`font-semibold ${profitMargin >= 20 ? 'text-emerald-600' : profitMargin >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      {profitMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Suggested Price with Editable Margin */}
              {form.monthly_price < suggestedMinPrice && form.billing_type === 'recurring' && (
                <div className="mt-3 p-2 bg-blue-100 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs text-blue-700">
                      <strong>Suggested min price:</strong> ${suggestedMinPrice.toFixed(2)}/mo for
                    </p>
                    <Input
                      type="number"
                      value={targetMargin}
                      onChange={(e) => setTargetMargin(parseFloat(e.target.value) || 0)}
                      className="w-16 h-6 text-xs bg-white border-blue-300 text-blue-700 px-2"
                      min={0}
                    />
                    <span className="text-xs text-blue-700">% margin</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs border-blue-300 text-blue-700 hover:bg-blue-200"
                    onClick={() => setForm({ ...form, monthly_price: suggestedMinPrice, annual_price: Math.round(suggestedMinPrice * 0.85 * 100) / 100 })}
                  >
                    Apply Suggested Price
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} className="border-slate-300 text-slate-700">Cancel</Button>
          <Button onClick={handleSave} disabled={form.monthly_price < estimatedCosts.total} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />{plan ? 'Update' : 'Create'} Plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanModal;
